import { VacancyMeetingMode } from 'api/endpoints/openapi-generated.schemas'
import {
  ClientToServerCallMessage,
  MeetingMode,
  ServerToClientCallMessage
} from './call/call-proto-generated'
import {
  callDialogService,
  CallStrategy
} from './interview/pages/call/call-dialog.service'
import { createEvent, restore, sample } from 'effector'
import { MeetingModeFe } from './interview/interview-ui.types'
import { callWsService } from './interview/pages/call/call-ws.service'
import { callWsStateService } from './interview/pages/call/call-ws-state.service'

export const mapModeToStrategy: {
  [K in MeetingMode]?: { mode: VacancyMeetingMode; strategy: CallStrategy }
} = {
  [MeetingMode.CHAT]: {
    mode: VacancyMeetingMode.chat_only,
    strategy: 'chat'
  },
  [MeetingMode.AUDIO]: {
    mode: VacancyMeetingMode.voice_only,
    strategy: '11labs'
  },
  [MeetingMode.VIDEO]: {
    mode: VacancyMeetingMode.voice_and_video,
    strategy: 'heygen'
  }
}

class MeetingModeSwitcherService {
  readonly setCurrentMeetingMode = createEvent<MeetingModeFe>()
  readonly $currentMeetingMode = restore(this.setCurrentMeetingMode, null)

  readonly switchMeetingMode = createEvent<MeetingMode>()

  constructor() {
    sample({
      clock: this.switchMeetingMode,
      source: callWsStateService.$callId,
      filter: Boolean,
      fn: (callId: string, meetingMode: MeetingMode): Uint8Array => {
        return ClientToServerCallMessage.encode({
          requestSwitchingMode: {
            callId,
            meetingMode,
            reason: 'SERVICE_UNAVAILABLE'
          }
        }).finish()
      },
      target: callWsService.sendMessageFx
    })
  }

  public requestSwitchMeetingMode(meetingMode: MeetingMode): void {
    this.switchMeetingMode(meetingMode)
  }

  private mapRawMeetingMode = (
    raw: MeetingMode
  ): { mode: VacancyMeetingMode; strategy: CallStrategy } | null => {
    return mapModeToStrategy[raw] ?? null
  }

  private handleWsMeetingMode(msg: ServerToClientCallMessage): void {
    const { switchMeetingMode } = msg

    if (!switchMeetingMode) return

    const mapped = this.mapRawMeetingMode(switchMeetingMode.meetingMode)
    if (!mapped) return

    callDialogService.strategy = mapped.strategy
    this.setCurrentMeetingMode(mapped.mode)
  }

  public subscribe(): VoidFunction {
    return callWsService.autoSubscribeToWsMsgOnOpen(
      'meeting-mode-switcher-service',
      (msg) => this.handleWsMeetingMode(msg)
    )
  }
}

export const meetingModeSwitcherService = new MeetingModeSwitcherService()
